import { jsPDF } from 'jspdf';
import autoTable, { CellHookData } from 'jspdf-autotable';
import { PunchItem, PunchStatus, PunchSeverity, PUNCH_VERIFIED_STATUS, PunchPhoto, Project } from '../types';

export type PunchPdfGroupBy = 'location' | 'trade' | 'status';

const STATUS_LABEL: Record<number, string> = {
  [PunchStatus.Open]: 'Open',
  [PunchStatus.InProgress]: 'In Progress',
  [PunchStatus.ReadyForInspection]: 'Ready For Inspection',
  [PunchStatus.Closed]: 'Closed',
};
const STATUS_ORDER = [PunchStatus.Open, PunchStatus.InProgress, PunchStatus.ReadyForInspection, PunchStatus.Closed];
const SEVERITY_LABEL: Record<number, string> = {
  [PunchSeverity.Minor]: 'Minor',
  [PunchSeverity.Major]: 'Major',
  [PunchSeverity.Critical]: 'Critical',
};

const BRAND = '#0E7490';
const INK = '#0F1117';
const INK3 = '#5A6272';

function isVerified(p: PunchItem): boolean {
  return p.dagrun_pm_status === PUNCH_VERIFIED_STATUS || !!p.dagrun_pm_verifiedon;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function parsePhotos(json?: string | null): PunchPhoto[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr.filter((p): p is PunchPhoto => p && typeof p.url === 'string');
  } catch {
    return [];
  }
}

// Fetch an image URL and return a data URL, or null on any failure.
async function loadImage(url: string): Promise<{ dataUrl: string; format: 'PNG' | 'JPEG' } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const format = /image\/png/i.test(dataUrl) ? 'PNG' : 'JPEG';
    return { dataUrl, format };
  } catch {
    return null;
  }
}

function groupKey(p: PunchItem, groupBy: PunchPdfGroupBy): string {
  if (groupBy === 'status') return STATUS_LABEL[p.dagrun_pm_status ?? PunchStatus.Open] ?? 'Open';
  if (groupBy === 'trade') return p.dagrun_pm_trade?.trim() || 'Unspecified Trade';
  return p.dagrun_pm_location?.trim() || 'Unspecified Location';
}

function orderedGroups(items: PunchItem[], groupBy: PunchPdfGroupBy): [string, PunchItem[]][] {
  const map = new Map<string, PunchItem[]>();
  items.forEach(p => {
    const k = groupKey(p, groupBy);
    (map.get(k) ?? map.set(k, []).get(k)!).push(p);
  });
  const entries = [...map.entries()];
  if (groupBy === 'status') {
    const rank = (k: string) => {
      const v = STATUS_ORDER.find(s => STATUS_LABEL[s] === k);
      return v != null ? STATUS_ORDER.indexOf(v) : 99;
    };
    entries.sort((a, b) => rank(a[0]) - rank(b[0]));
  } else {
    // Alphabetical, but push the "Unspecified …" bucket to the end.
    entries.sort((a, b) => {
      const au = a[0].startsWith('Unspecified'), bu = b[0].startsWith('Unspecified');
      if (au !== bu) return au ? 1 : -1;
      return a[0].localeCompare(b[0]);
    });
  }
  return entries;
}

/**
 * Export a punch list to PDF, grouped by location, trade, or status.
 * Photo thumbnails (up to 3 per item) render below that item's row when
 * dagrun_pm_photosjson is populated; images that fail to fetch are skipped.
 *
 * Signature is synchronous (returns void) per spec — the async image
 * preloading + save runs in a detached promise.
 */
export function exportPunchListPdf(items: PunchItem[], project: Project, groupBy: PunchPdfGroupBy): void {
  void buildAndSave(items, project, groupBy);
}

const THUMB = 16;       // mm — thumbnail square size
const THUMB_GAP = 2;    // mm — gap between thumbnails
const PHOTO_ROW_PAD = THUMB + 4; // extra row height reserved for the photo strip

async function buildAndSave(items: PunchItem[], project: Project, groupBy: PunchPdfGroupBy): Promise<void> {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 12;

  // ── Header ──
  const openCount = items.filter(p => !isVerified(p)).length;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(INK);
  doc.text(`${project.dagrun_pm_name} — Punch List`, marginX, 16);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(INK3);
  const exportedOn = new Date().toLocaleString();
  doc.text(`Exported ${exportedOn}    ·    ${items.length} item${items.length === 1 ? '' : 's'}    ·    ${openCount} open`, marginX, 22);

  // ── Preload photos (best-effort, up to 3 per item) ──
  const photoMap = new Map<string, { dataUrl: string; format: 'PNG' | 'JPEG' }[]>();
  await Promise.all(items.map(async p => {
    const photos = parsePhotos(p.dagrun_pm_photosjson).slice(0, 3);
    if (photos.length === 0) return;
    const loaded = await Promise.all(photos.map(ph => loadImage(ph.url)));
    const ok = loaded.filter((x): x is { dataUrl: string; format: 'PNG' | 'JPEG' } => x != null);
    if (ok.length > 0) photoMap.set(p.dagrun_pm_punchitemid, ok);
  }));

  let cursorY = 28;
  const groups = orderedGroups(items, groupBy);

  for (const [label, groupItems] of groups) {
    // Section header.
    if (cursorY > doc.internal.pageSize.getHeight() - 30) { doc.addPage(); cursorY = 18; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(BRAND);
    doc.text(`${label}  (${groupItems.length})`, marginX, cursorY);
    cursorY += 2;

    const rowItems = groupItems; // index alignment for hooks

    autoTable(doc, {
      startY: cursorY + 2,
      margin: { left: marginX, right: marginX },
      head: [['#', 'Title', 'Location', 'Trade', 'Assigned To', 'Due', 'Severity', 'Status', 'Verified By']],
      body: groupItems.map(p => [
        p.dagrun_pm_itemnumber != null ? String(p.dagrun_pm_itemnumber) : '—',
        p.dagrun_pm_name ?? '',
        p.dagrun_pm_location ?? '—',
        p.dagrun_pm_trade ?? '—',
        p.dagrun_pm_assigneename ?? '—',
        fmtDate(p.dagrun_pm_duedate),
        p.dagrun_pm_severity != null ? (SEVERITY_LABEL[p.dagrun_pm_severity] ?? '—') : '—',
        STATUS_LABEL[p.dagrun_pm_status ?? PunchStatus.Open] ?? '—',
        p.dagrun_pm_verifiedby ?? (isVerified(p) ? 'Verified' : '—'),
      ]),
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 1.6, textColor: INK, overflow: 'linebreak' },
      headStyles: { fillColor: [14, 116, 144], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 246, 248] },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 60 } },
      // Reserve extra height on rows that carry photo thumbnails.
      didParseCell: (data: CellHookData) => {
        if (data.section !== 'body') return;
        const item = rowItems[data.row.index];
        if (item && photoMap.has(item.dagrun_pm_punchitemid)) {
          data.cell.styles.minCellHeight = (data.cell.styles.minCellHeight ?? 0) + PHOTO_ROW_PAD;
        }
      },
      // Draw thumbnails along the bottom of the row, under the first columns.
      didDrawCell: (data: CellHookData) => {
        if (data.section !== 'body' || data.column.index !== 0) return;
        const item = rowItems[data.row.index];
        if (!item) return;
        const imgs = photoMap.get(item.dagrun_pm_punchitemid);
        if (!imgs || imgs.length === 0) return;
        const y = data.cell.y + data.cell.height - THUMB - 2;
        let x = data.cell.x + 1;
        imgs.forEach(img => {
          try { doc.addImage(img.dataUrl, img.format, x, y, THUMB, THUMB); } catch { /* skip */ }
          x += THUMB + THUMB_GAP;
        });
      },
    });

    // @ts-expect-error lastAutoTable is attached by the plugin at runtime.
    cursorY = (doc.lastAutoTable?.finalY ?? cursorY + 20) + 8;
  }

  // ── Footer: page X of Y ──
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(INK3);
    const h = doc.internal.pageSize.getHeight();
    doc.text(`Page ${i} of ${total}`, pageW - marginX, h - 6, { align: 'right' });
    doc.text(`${project.dagrun_pm_name} · Punch List`, marginX, h - 6);
  }

  const safe = (project.dagrun_pm_name || 'project').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  doc.save(`punch-list-${safe}-${groupBy}.pdf`);
}
